import React, { useCallback, useEffect, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { RefreshCw, Shield } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const FIC_SOURCES = [
  { href: 'https://www.fic.gov.za/targeted-financial-sanctions/', label: 'FIC TFS page' },
  { href: 'https://tfs.fic.gov.za/Pages/TFSListDownload', label: 'Download list' },
  { href: 'https://tfs.fic.gov.za/Pages/Subscriptions', label: 'Email alerts' },
];

const API = axios.create({ baseURL: '/api/v1/admin/fic-tfs' });
API.interceptors.request.use((cfg) => {
  const token = sessionStorage.getItem('portal_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

type AlertStatus =
  | 'decision'
  | 'open'
  | 'investigating'
  | 'confirmed'
  | 'false_positive_proposed'
  | 'false_positive'
  | 'reported'
  | '';

interface TfsHealth {
  colour: 'red' | 'amber' | 'green' | string;
  label: string;
  detail: string;
  lastCheckedAt: string | null;
  openDecisionCount: number;
  personCount: number;
  entityCount: number;
}

interface TfsStatus {
  latestVersion: {
    id: number;
    source: string;
    sha256: string;
    entry_count: number;
    person_count: number;
    entity_count: number;
    parse_status: string;
    fetched_at: string;
  } | null;
  lastJobRun?: { outcome: string; checked_at: string; error_message: string | null } | null;
  health?: TfsHealth;
  openAlerts: { match_class: string; count: number }[];
  openPublicationReviews: number;
  openDecisionCount?: number;
  screeningEnabled: boolean;
  dryRun: boolean;
  enforcementEnabled?: boolean;
  pepEddOpen?: number;
}

interface TfsAlert {
  id: number;
  status: string;
  match_class: string;
  subject_type: string;
  subject_id: string;
  entry_reference: string | null;
  notes: string | null;
  goaml_reference: string | null;
  confirmed_match_at: string | null;
  created_at: string;
  score: string | number | null;
}

interface PepEddTask {
  id: number;
  subject_type: string;
  subject_id: string;
  status: string;
  source: string;
  created_at: string;
}

interface PepSubject {
  subjectType: string;
  subjectId: string;
  displayName: string;
  phoneNumber: string | null;
  email: string | null;
  accountStatus: string | null;
  kycStatus: string | null;
  kycTier: number | null;
  pepDeclared: boolean | null;
  pepDeclaredAt: string | null;
  pepStaffFlag: boolean | null;
  pepStaffFlaggedAt: string | null;
  pepStaffFlaggedBy: string | null;
  walletBalance: string | null;
  walletCurrency: string | null;
  walletStatus: string | null;
  openTask: { id: number; source: string; created_at: string } | null;
}

interface PublicationReview {
  id: number;
  source_label: string;
  source_url: string;
  suggested_pol: string[] | string;
  status: string;
  created_at: string;
}

function errorMessage(error: unknown, fallback: string): string {
  const axiosError = error as AxiosError<{ error?: string; message?: string }>;
  return axiosError.response?.data?.error || axiosError.response?.data?.message || fallback;
}

function fmtDate(d: string | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function ageLabel(d: string): string {
  const days = Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return '1 day';
  return `${days} days`;
}

function reportingClock(alert: TfsAlert): string | null {
  if (alert.status !== 'confirmed' || !alert.confirmed_match_at) return null;
  const deadline = new Date(alert.confirmed_match_at).getTime() + 5 * 86400000;
  const days = Math.ceil((deadline - Date.now()) / 86400000);
  if (days < 0) return 'goAML overdue';
  if (days === 0) return 'goAML due today';
  return `goAML due in ${days} day${days === 1 ? '' : 's'}`;
}

function statusClass(status: string): string {
  switch (status) {
    case 'confirmed':
      return 'bg-red-50 text-red-700';
    case 'likely':
    case 'open':
    case 'investigating':
      return 'bg-amber-50 text-amber-800';
    case 'false_positive':
    case 'clear':
      return 'bg-emerald-50 text-emerald-700';
    case 'false_positive_proposed':
      return 'bg-orange-50 text-orange-800';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

function defaultReviewDate(): string {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() + 90);
  return date.toISOString().slice(0, 10);
}

function yesNo(value: boolean | null | undefined): string {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return 'Not recorded';
}

function moneyLabel(amount: string | null, currency: string | null): string {
  if (amount == null) return '—';
  const value = Number(amount);
  if (!Number.isFinite(value)) return amount;
  return `${currency || 'ZAR'} ${value.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function bandClass(colour: string | undefined): string {
  if (colour === 'red') return 'border-red-200 bg-red-50';
  if (colour === 'amber') return 'border-amber-200 bg-amber-50';
  if (colour === 'green') return 'border-emerald-200 bg-emerald-50';
  return 'border-[var(--border)] bg-[var(--card)]';
}

export const FicTfsOverlay: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [status, setStatus] = useState<TfsStatus | null>(null);
  const [alerts, setAlerts] = useState<TfsAlert[]>([]);
  const [reviews, setReviews] = useState<PublicationReview[]>([]);
  const [pepTasks, setPepTasks] = useState<PepEddTask[]>([]);
  const [pepSubjectType, setPepSubjectType] = useState<'user' | 'cp'>('user');
  const [pepSubjectId, setPepSubjectId] = useState('');
  const [pepFlagged, setPepFlagged] = useState<'yes' | 'no'>('no');
  const [pepSubject, setPepSubject] = useState<PepSubject | null>(null);
  const [pepLooking, setPepLooking] = useState(false);
  const [publicRole, setPublicRole] = useState('');
  const [sourceOfFunds, setSourceOfFunds] = useState('');
  const [continueRelationship, setContinueRelationship] = useState<'yes' | 'no'>('yes');
  const [nextReviewOn, setNextReviewOn] = useState(defaultReviewDate);
  const [alertFilter, setAlertFilter] = useState<AlertStatus>('decision');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [checking, setChecking] = useState(false);
  const [reason, setReason] = useState('');
  const [goamlReference, setGoamlReference] = useState('');
  const [tprJson, setTprJson] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '25' });
      if (alertFilter) params.set('status', alertFilter);
      const [statusRes, alertsRes, reviewsRes] = await Promise.all([
        API.get('/status'),
        API.get(`/alerts?${params.toString()}`),
        API.get('/publication-reviews'),
      ]);
      setStatus(statusRes.data.data);
      setAlerts(alertsRes.data.data || []);
      setTotal(alertsRes.data.pagination?.total || 0);
      setReviews(reviewsRes.data.data || []);
      try {
        const pepRes = await API.get('/pep-edd');
        setPepTasks(pepRes.data.data || []);
      } catch {
        setPepTasks([]);
      }
    } catch (err) {
      setError(errorMessage(err, 'Unable to load FIC TFS data'));
    } finally {
      setLoading(false);
    }
  }, [page, alertFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const runAction = async (id: number, action: string) => {
    const trimmed = reason.trim();
    if (action !== 'investigate' && trimmed.length < 8) {
      setError('Enter a short reason before you record a decision.');
      return;
    }
    if (action === 'reported' && !/^[A-Za-z0-9][A-Za-z0-9._:/-]{2,79}$/.test(goamlReference.trim())) {
      setError('Enter the goAML reference after you have filed the report.');
      return;
    }
    setBusyId(id);
    setError(null);
    try {
      await API.patch(`/alerts/${id}`, {
        action,
        notes: trimmed || undefined,
        goamlReference: action === 'reported' ? goamlReference.trim() : undefined,
      });
      setReason('');
      if (action === 'reported') setGoamlReference('');
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Unable to update alert'));
    } finally {
      setBusyId(null);
    }
  };

  const downloadTpr = async (id: number) => {
    setBusyId(id);
    setError(null);
    try {
      const res = await API.get(`/alerts/${id}/tpr-pack`);
      setTprJson(JSON.stringify(res.data.data, null, 2));
    } catch (err) {
      setError(errorMessage(err, 'Unable to build TPR pack'));
    } finally {
      setBusyId(null);
    }
  };

  const checkList = async () => {
    setChecking(true);
    setError(null);
    try {
      await API.post('/check-list', {});
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Unable to check the FIC list'));
    } finally {
      setChecking(false);
    }
  };

  const lookupPep = async (subjectType: 'user' | 'cp', subjectId: string) => {
    if (!subjectId.trim()) {
      setError('Enter the user id from the PEP row');
      return;
    }
    setPepLooking(true);
    setError(null);
    setPepSubject(null);
    try {
      const res = await API.get('/pep-subject', { params: { subjectType, subjectId } });
      setPepSubject(res.data.data);
      setPublicRole('');
      setSourceOfFunds('');
      setContinueRelationship('yes');
      setNextReviewOn(defaultReviewDate());
    } catch (err) {
      setError(errorMessage(err, 'Unable to look up this user'));
    } finally {
      setPepLooking(false);
    }
  };

  const closePepTask = async (id: number) => {
    setBusyId(id);
    setError(null);
    try {
      await API.patch(`/pep-edd/${id}`, {
        publicRole,
        sourceOfFunds,
        continueRelationship,
        nextReviewOn,
      });
      setPepSubject(null);
      setPublicRole('');
      setSourceOfFunds('');
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Unable to close PEP EDD task'));
    } finally {
      setBusyId(null);
    }
  };

  const setPepStaffFlag = async () => {
    if (!pepSubjectId.trim()) {
      setError('Enter a user or partner id to set the PEP staff flag');
      return;
    }
    setBusyId(-1);
    setError(null);
    try {
      await API.post('/pep-flag', {
        subjectType: pepSubjectType,
        subjectId: Number(pepSubjectId),
        flagged: pepFlagged === 'yes',
      });
      const res = await API.get('/pep-subject', {
        params: { subjectType: pepSubjectType, subjectId: pepSubjectId },
      });
      setPepSubject(res.data.data);
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Unable to set PEP staff flag'));
    } finally {
      setBusyId(null);
    }
  };

  const updateReview = async (id: number, nextStatus: string) => {
    setBusyId(id);
    setError(null);
    try {
      await API.patch(`/publication-reviews/${id}`, { status: nextStatus });
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Unable to update publication review'));
    } finally {
      setBusyId(null);
    }
  };

  const health = status?.health;
  const version = status?.latestVersion;
  const totalPages = Math.max(1, Math.ceil(total / 25));
  const modeLine = status?.enforcementEnabled
    ? 'Live holds are on. A confirmed match can suspend a wallet.'
    : 'Alert only. Wallets are not frozen. goAML is filed by a person.';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--foreground)]">FIC Targeted Financial Sanctions</h1>
          <p className="mt-1 max-w-3xl text-sm text-[var(--muted-foreground)]">{modeLine}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && (
            <button
              type="button"
              disabled={checking}
              onClick={() => void checkList()}
              className="inline-flex items-center gap-2 rounded-lg border border-[var(--border)] bg-[var(--foreground)] px-3 py-2 text-sm font-medium text-[var(--background)] disabled:opacity-50"
            >
              {checking ? 'Checking list…' : 'Check list now'}
            </button>
          )}
          <button
            type="button"
            onClick={() => void load()}
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-2 text-sm font-medium text-[var(--foreground)] hover:bg-[var(--muted)]"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <section className={`rounded-xl border px-4 py-4 ${bandClass(health?.colour)}`}>
        <p className="text-lg font-semibold text-[var(--foreground)]">
          {health?.label || (loading ? 'Loading list health' : 'List health unavailable')}
        </p>
        <p className="mt-1 text-sm text-[var(--foreground)]">
          {health?.detail || 'The status service did not return a health colour.'}
        </p>
        <p className="mt-2 text-xs text-[var(--muted-foreground)]">
          {health
            ? `${health.personCount} persons · ${health.entityCount} entities · last check ${fmtDate(health.lastCheckedAt)} · ${health.openDecisionCount} open decision${health.openDecisionCount === 1 ? '' : 's'}`
            : version
              ? `${version.person_count} persons · ${version.entity_count} entities`
              : 'No list snapshot'}
          {status?.screeningEnabled ? '' : ' · screening is off'}
        </p>
      </section>

      <section className="rounded-xl border border-[var(--border)] bg-[var(--card)]">
        <div className="flex flex-wrap items-end justify-between gap-3 border-b border-[var(--border)] px-4 py-3">
          <div>
            <h2 className="text-base font-semibold text-[var(--foreground)]">Decisions</h2>
            <p className="mt-1 text-xs text-[var(--muted-foreground)]">
              Likely and confirmed matches email support. Possible matches stay in this queue until the morning digest.
            </p>
          </div>
          <label className="text-sm text-[var(--muted-foreground)]">
            Show
            <select
              className="ml-2 rounded-md border border-[var(--border)] bg-[var(--background)] px-2 py-1 text-sm text-[var(--foreground)]"
              value={alertFilter}
              onChange={(e) => {
                setPage(1);
                setAlertFilter(e.target.value as AlertStatus);
              }}
            >
              <option value="decision">Needs a decision</option>
              <option value="open">Open</option>
              <option value="investigating">Investigating</option>
              <option value="false_positive_proposed">False positive proposed</option>
              <option value="confirmed">Confirmed</option>
              <option value="false_positive">False positive</option>
              <option value="reported">Reported</option>
              <option value="">All</option>
            </select>
          </label>
        </div>
        <div className="grid gap-3 border-b border-[var(--border)] px-4 py-3 sm:grid-cols-2">
          <label className="text-sm text-[var(--muted-foreground)]">
            Reason
            <input
              className="mt-1 w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-2 py-1 text-sm text-[var(--foreground)]"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={2000}
              placeholder="Required for a match decision"
            />
          </label>
          {isAdmin && (
            <label className="text-sm text-[var(--muted-foreground)]">
              goAML reference
              <input
                className="mt-1 w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-2 py-1 text-sm text-[var(--foreground)]"
                value={goamlReference}
                onChange={(e) => setGoamlReference(e.target.value)}
                maxLength={80}
                placeholder="After you file, not before"
              />
            </label>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-[var(--muted)] text-xs uppercase tracking-wide text-[var(--muted-foreground)]">
              <tr>
                <th className="px-4 py-2 font-medium">Id</th>
                <th className="px-4 py-2 font-medium">Class</th>
                <th className="px-4 py-2 font-medium">Subject</th>
                <th className="px-4 py-2 font-medium">TFS ref</th>
                <th className="px-4 py-2 font-medium">Score</th>
                <th className="px-4 py-2 font-medium">Age</th>
                <th className="px-4 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {alerts.length === 0 && !loading && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-[var(--muted-foreground)]">
                    Nothing needs a decision in this filter.
                  </td>
                </tr>
              )}
              {alerts.map((alert) => {
                const clock = reportingClock(alert);
                return (
                  <tr key={alert.id} className="border-t border-[var(--border)]">
                    <td className="px-4 py-3 tabular-nums">{alert.id}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(alert.match_class)}`}>
                        {alert.match_class}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {alert.subject_type} #{alert.subject_id}
                      <div className="text-xs text-[var(--muted-foreground)]">{alert.status}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{alert.entry_reference || '—'}</td>
                    <td className="px-4 py-3 tabular-nums text-xs">{alert.score ?? '—'}</td>
                    <td className="px-4 py-3 text-xs text-[var(--muted-foreground)]">
                      {ageLabel(alert.created_at)}
                      {clock && <div className="mt-1 font-medium text-red-700">{clock}</div>}
                      {alert.goaml_reference && <div className="mt-1">goAML {alert.goaml_reference}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          disabled={busyId === alert.id}
                          className="rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-[var(--muted)]"
                          onClick={() => void runAction(alert.id, 'investigate')}
                        >
                          Investigate
                        </button>
                        {isAdmin && alert.status !== 'confirmed' && alert.status !== 'reported' && (
                          <button
                            type="button"
                            disabled={busyId === alert.id}
                            className="rounded-md border border-red-200 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                            onClick={() => void runAction(alert.id, 'confirm')}
                          >
                            Confirm match
                          </button>
                        )}
                        {alert.status !== 'false_positive' && alert.status !== 'reported' && (
                          <button
                            type="button"
                            disabled={busyId === alert.id}
                            className="rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-[var(--muted)]"
                            onClick={() => void runAction(alert.id, 'propose_false_positive')}
                          >
                            Propose false match
                          </button>
                        )}
                        {isAdmin && alert.status === 'false_positive_proposed' && (
                          <button
                            type="button"
                            disabled={busyId === alert.id}
                            className="rounded-md border border-emerald-200 px-2 py-1 text-xs text-emerald-800 hover:bg-emerald-50"
                            onClick={() => void runAction(alert.id, 'confirm_false_positive')}
                          >
                            Confirm false match
                          </button>
                        )}
                        {isAdmin && alert.status === 'confirmed' && (
                          <button
                            type="button"
                            disabled={busyId === alert.id}
                            className="rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-[var(--muted)]"
                            onClick={() => void runAction(alert.id, 'reported')}
                          >
                            Mark reported
                          </button>
                        )}
                        <button
                          type="button"
                          disabled={busyId === alert.id}
                          className="rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-[var(--muted)]"
                          onClick={() => void downloadTpr(alert.id)}
                        >
                          TPR pack
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-[var(--border)] px-4 py-3 text-sm text-[var(--muted-foreground)]">
          <span>
            Page {page} of {totalPages} ({total} alerts)
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              className="rounded-md border border-[var(--border)] px-2 py-1 disabled:opacity-40"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <button
              type="button"
              className="rounded-md border border-[var(--border)] px-2 py-1 disabled:opacity-40"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </section>

      {tprJson && (
        <section className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-4">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-base font-semibold text-[var(--foreground)]">Masked TPR pack</h2>
            <button type="button" className="text-sm text-[var(--muted-foreground)]" onClick={() => setTprJson(null)}>
              Close
            </button>
          </div>
          <p className="mb-2 text-xs text-[var(--muted-foreground)]">
            No names or identity numbers. File this in goAML yourself, then record the reference above.
          </p>
          <pre className="overflow-x-auto rounded-lg bg-[var(--muted)] p-3 text-xs text-[var(--foreground)]">{tprJson}</pre>
        </section>
      )}

      <details open className="rounded-xl border border-[var(--border)] bg-[var(--card)]">
        <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-[var(--foreground)]">
          PEP enhanced due diligence ({status?.pepEddOpen ?? pepTasks.length} open)
        </summary>
        <p className="px-4 pb-3 text-xs text-[var(--muted-foreground)]">
          Press Review on a row, or type the user id. Confirm the person on the card, write the four answers, then save. Saving stores the note and closes the task. It does not freeze the wallet.
        </p>
        <form
          className="flex flex-wrap items-end gap-3 border-t border-[var(--border)] px-4 py-3"
          onSubmit={(event) => {
            event.preventDefault();
            void lookupPep(pepSubjectType, pepSubjectId);
          }}
        >
          <label className="text-sm text-[var(--muted-foreground)]">
            Subject
            <select
              className="ml-2 rounded-md border border-[var(--border)] bg-[var(--background)] px-2 py-1 text-sm text-[var(--foreground)]"
              value={pepSubjectType}
              onChange={(e) => setPepSubjectType(e.target.value as 'user' | 'cp')}
            >
              <option value="user">Wallet user</option>
              <option value="cp">Partner</option>
            </select>
          </label>
          <label className="text-sm text-[var(--muted-foreground)]">
            User id
            <input
              className="ml-2 w-28 rounded-md border border-[var(--border)] bg-[var(--background)] px-2 py-1 text-sm text-[var(--foreground)]"
              value={pepSubjectId}
              onChange={(e) => setPepSubjectId(e.target.value.replace(/\D/g, ''))}
              inputMode="numeric"
              placeholder="407"
            />
          </label>
          <button
            type="submit"
            disabled={pepLooking}
            className="rounded-md bg-[var(--foreground)] px-3 py-1 text-sm text-[var(--background)] disabled:opacity-40"
          >
            {pepLooking ? 'Looking up…' : 'Look up'}
          </button>
        </form>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <tbody>
              {pepTasks.length === 0 && (
                <tr>
                  <td className="px-4 py-6 text-center text-[var(--muted-foreground)]">No open PEP EDD tasks.</td>
                </tr>
              )}
              {pepTasks.map((task) => (
                <tr key={task.id} className="border-t border-[var(--border)]">
                  <td className="px-4 py-3 tabular-nums">{task.id}</td>
                  <td className="px-4 py-3">{task.subject_type} #{task.subject_id}</td>
                  <td className="px-4 py-3">{task.source}</td>
                  <td className="px-4 py-3 text-xs text-[var(--muted-foreground)]">{fmtDate(task.created_at)}</td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      className="rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-[var(--muted)]"
                      onClick={() => {
                        const kind = task.subject_type === 'cp' ? 'cp' : 'user';
                        setPepSubjectType(kind);
                        setPepSubjectId(String(task.subject_id));
                        void lookupPep(kind, String(task.subject_id));
                      }}
                    >
                      Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pepSubject && (
          <div className="space-y-4 border-t border-[var(--border)] px-4 py-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-[var(--foreground)]">{pepSubject.displayName}</h3>
                <p className="text-xs text-[var(--muted-foreground)]">
                  {pepSubject.subjectType} #{pepSubject.subjectId}
                  {pepSubject.phoneNumber ? ` · ${pepSubject.phoneNumber}` : ''}
                  {pepSubject.email ? ` · ${pepSubject.email}` : ''}
                </p>
              </div>
              {pepSubject.subjectType === 'user' && (
                <a
                  href={`/admin/users?highlightUserId=${pepSubject.subjectId}`}
                  className="text-sm underline-offset-2 hover:underline"
                >
                  Open full user record
                </a>
              )}
            </div>
            <dl className="grid gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-xs text-[var(--muted-foreground)]">Account</dt>
                <dd>{pepSubject.accountStatus || '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-[var(--muted-foreground)]">KYC</dt>
                <dd>
                  {pepSubject.kycStatus || '—'}
                  {pepSubject.kycTier != null ? ` · tier ${pepSubject.kycTier}` : ''}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-[var(--muted-foreground)]">Customer said PEP</dt>
                <dd>
                  {yesNo(pepSubject.pepDeclared)}
                  {pepSubject.pepDeclaredAt ? ` · ${fmtDate(pepSubject.pepDeclaredAt)}` : ''}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-[var(--muted-foreground)]">Staff flag</dt>
                <dd>
                  {yesNo(pepSubject.pepStaffFlag)}
                  {pepSubject.pepStaffFlaggedBy ? ` · ${pepSubject.pepStaffFlaggedBy}` : ''}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-[var(--muted-foreground)]">Wallet balance</dt>
                <dd>{moneyLabel(pepSubject.walletBalance, pepSubject.walletCurrency)}</dd>
              </div>
              <div>
                <dt className="text-xs text-[var(--muted-foreground)]">Open task</dt>
                <dd>{pepSubject.openTask ? `#${pepSubject.openTask.id} · ${pepSubject.openTask.source}` : 'None'}</dd>
              </div>
            </dl>
            {pepSubject.openTask ? (
              <div className="grid gap-3">
                <label className="text-sm text-[var(--muted-foreground)]">
                  Public role (the customer, a family member, or a close associate)
                  <input
                    className="mt-1 w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-2 py-1 text-sm text-[var(--foreground)]"
                    value={publicRole}
                    onChange={(e) => setPublicRole(e.target.value)}
                  />
                </label>
                <label className="text-sm text-[var(--muted-foreground)]">
                  Where the money in the wallet comes from
                  <input
                    className="mt-1 w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-2 py-1 text-sm text-[var(--foreground)]"
                    value={sourceOfFunds}
                    onChange={(e) => setSourceOfFunds(e.target.value)}
                  />
                </label>
                <label className="text-sm text-[var(--muted-foreground)]">
                  Continue this account
                  <select
                    className="ml-2 rounded-md border border-[var(--border)] bg-[var(--background)] px-2 py-1 text-sm text-[var(--foreground)]"
                    value={continueRelationship}
                    onChange={(e) => setContinueRelationship(e.target.value as 'yes' | 'no')}
                  >
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </label>
                <label className="text-sm text-[var(--muted-foreground)]">
                  Next review
                  <input
                    type="date"
                    className="ml-2 rounded-md border border-[var(--border)] bg-[var(--background)] px-2 py-1 text-sm text-[var(--foreground)]"
                    value={nextReviewOn}
                    onChange={(e) => setNextReviewOn(e.target.value)}
                  />
                </label>
                <p className="text-xs text-[var(--muted-foreground)]">
                  Choosing no records that decision. It does not close the wallet. The next review must be about three months from today.
                </p>
                <button
                  type="button"
                  disabled={busyId === pepSubject.openTask.id}
                  className="w-fit rounded-md bg-[var(--foreground)] px-3 py-1.5 text-sm text-[var(--background)] disabled:opacity-40"
                  onClick={() => void closePepTask(pepSubject.openTask!.id)}
                >
                  Save EDD and close task
                </button>
              </div>
            ) : (
              <p className="text-sm text-[var(--muted-foreground)]">This id has no open PEP task.</p>
            )}
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-sm text-[var(--muted-foreground)]">
                If the yes was a mistake, set the staff flag
                <select
                  className="ml-2 rounded-md border border-[var(--border)] bg-[var(--background)] px-2 py-1 text-sm text-[var(--foreground)]"
                  value={pepFlagged}
                  onChange={(e) => setPepFlagged(e.target.value as 'yes' | 'no')}
                >
                  <option value="no">PEP no</option>
                  <option value="yes">PEP yes</option>
                </select>
              </label>
              <button
                type="button"
                disabled={busyId === -1}
                className="rounded-md border border-[var(--border)] px-3 py-1 text-sm hover:bg-[var(--muted)]"
                onClick={() => void setPepStaffFlag()}
              >
                Save staff flag
              </button>
            </div>
          </div>
        )}
      </details>

      <details className="rounded-xl border border-[var(--border)] bg-[var(--card)]">
        <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-[var(--foreground)]">
          FIC site log ({status?.openPublicationReviews ?? 0} download-page reviews)
        </summary>
        <p className="px-4 pb-3 text-xs text-[var(--muted-foreground)]">
          Only a change to the TFS download page opens a review. Other FIC pages are logged. Policies are not edited from here.
        </p>
        <div className="overflow-x-auto border-t border-[var(--border)]">
          <table className="min-w-full text-left text-sm">
            <tbody>
              {reviews.length === 0 && (
                <tr>
                  <td className="px-4 py-6 text-center text-[var(--muted-foreground)]">No publication log yet.</td>
                </tr>
              )}
              {reviews.map((review) => (
                <tr key={review.id} className="border-t border-[var(--border)]">
                  <td className="px-4 py-3">
                    <div className="font-medium text-[var(--foreground)]">{review.source_label}</div>
                    <a
                      href={review.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block max-w-xs truncate text-xs underline-offset-2 hover:underline"
                    >
                      {review.source_url}
                    </a>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {Array.isArray(review.suggested_pol) ? review.suggested_pol.join(', ') : String(review.suggested_pol || '—')}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(review.status)}`}>
                      {review.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-[var(--muted-foreground)]">{fmtDate(review.created_at)}</td>
                  <td className="px-4 py-3">
                    {isAdmin && review.status === 'open' && (
                      <div className="flex flex-wrap gap-2">
                        <button type="button" className="rounded-md border border-[var(--border)] px-2 py-1 text-xs" onClick={() => void updateReview(review.id, 'dismissed')}>
                          Dismiss
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="flex flex-wrap gap-x-4 gap-y-1 px-4 py-3 text-xs">
          {FIC_SOURCES.map((link) => (
            <a key={link.href} href={link.href} target="_blank" rel="noopener noreferrer" className="underline-offset-2 hover:underline">
              {link.label}
            </a>
          ))}
        </p>
      </details>

      <p className="flex items-center gap-2 text-xs text-[var(--muted-foreground)]">
        <Shield className="h-3.5 w-3.5" aria-hidden />
        A false match needs a written reason and a second admin. A true match is confirmed by an admin, then filed in goAML.
      </p>
    </div>
  );
};
